import type { ReactNode } from "react";
import { bgVar, fgVar, type StatusKey } from "@/lib/status-colors";
import { cn } from "@/lib/utils";

interface StatusBadgeProps {
  // The status concept to render. Use claimStatusKey(...) / policyStatusKey(...)
  // from @/lib/status-colors to resolve a raw status string to a key.
  statusKey: StatusKey;
  // The text shown inside the pill. Typically the raw status string from
  // the server (e.g. "Open", "Non-Renewed") so wording stays faithful to
  // the source data; the StatusKey only controls the color.
  label: string;
  className?: string;
  // Renders a small filled circle in front of the label. On by default; turn
  // off in tight rows where the dot would just add noise.
  withDot?: boolean;
  // Optional leading element (e.g. a Lock icon for the Restricted badge).
  // Renders before the label; suppresses the dot automatically.
  children?: ReactNode;
}

export function StatusBadge({
  statusKey,
  label,
  className,
  withDot = true,
  children,
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium text-xs",
        className,
      )}
      style={{
        backgroundColor: `var(${bgVar(statusKey)})`,
        color: `var(${fgVar(statusKey)})`,
      }}
    >
      {children}
      {!children && withDot && (
        <span
          className="inline-block size-1.5 rounded-full"
          style={{ backgroundColor: `var(${fgVar(statusKey)})` }}
          aria-hidden="true"
        />
      )}
      {label}
    </span>
  );
}
